/**
 * Trace Viewer Inspector v2 Extension
 * Adds a custom side pane with trace-viewer component controls
 */

import React, { useState } from 'react';
import { HDRCubeTexture } from '@babylonjs/core/Materials/Textures/hdrCubeTexture';
import { ENVIRONMENT_PRESETS as environmentPresets, type EnvironmentPreset } from './ai/spatial-environment';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
// Side-effect import to add createDefaultSkybox to Scene prototype
import '@babylonjs/core/Helpers/sceneHelpers';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { FramingBehavior } from '@babylonjs/core/Behaviors/Cameras/framingBehavior';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { GridMaterial } from '@babylonjs/materials/grid/gridMaterial';
import { setViewMode, type ViewMode } from './view-mode';
import {
    ShellServiceIdentity,
    type ServiceDefinition,
    type IShellService,
    type SidePaneDefinition
} from '@babylonjs/inspector';


// Environment map presets. Defined in src/ai/spatial-environment.ts so the
// panel and build_spatial's environment() name the same vocabulary — and so
// the bench half can read them without pulling in @babylonjs/inspector.
export { ENVIRONMENT_PRESETS as environmentPresets } from './ai/spatial-environment';
export type { EnvironmentPreset } from './ai/spatial-environment';

// Shell service ref (survives inspector reloads, allows external access)
let shellServiceRef: IShellService | null = null;

export function getShellService(): IShellService | null {
    return shellServiceRef;
}

// Persistent state store (survives inspector reloads)
// Exported so editor-runner can access it
export const settingsStore = {
    backgroundColor: '#2f2d2d',
    cameraRadius: 1,
    cameraBeta: 1.4, // pitch; look down from an angle
    alpha: 1,
    prewarmParticles: 0,
    environmentPreset: 'neutral' as EnvironmentPreset,
    environmentUrl: '',
    iblIntensity: 0.5,
    envRotation: 0,
    exposure: 1,
    contrast: 1,
    toneMappingEnabled: true,
    toneMappingType: 0, // 0 = Standard, 1 = ACES
    showSkybox: false,
    groundShadow: true,
    gridRatio: 0.1,
    gridMajorUnitFrequency: 10,
    gridMinorUnitVisibility: 0.3,
    gridOpacity: 0.8,
    logger: 'log', // milestone logs + warnings; 'enabled' for per-object debug spam
    debugSelectAnyNode: false,
    viewMode: 'shaded' as ViewMode
};

/**
 * Apply stored settings to the current scene.
 * Call this after a new scene loads.
 */
export const applyStoredSettings = () => {
    const viewer = document.querySelector('trace-viewer');
    if (!viewer?.scene) return;

    const scene = viewer.scene;

    // Apply background color
    const hex = settingsStore.backgroundColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    scene.clearColor.set(r, g, b, settingsStore.alpha);

    // Apply skybox if enabled and scene has an environment texture
    if (settingsStore.showSkybox && scene.environmentTexture) {
        // Remove any existing skybox first
        const existingSkybox = scene.getMeshByName('hdrSkyBox');
        if (existingSkybox) {
            existingSkybox.dispose();
        }
        scene.createDefaultSkybox(scene.environmentTexture, true, 10000, 0.1);
        console.log('[Inspector Extension] Created skybox for new scene');
    }

    setViewMode(scene, settingsStore.viewMode);

    // Apply IBL intensity
    scene.environmentIntensity = settingsStore.iblIntensity;

    // Apply environment rotation (environmentTexture is always a CubeTexture subclass)
    if (scene.environmentTexture && 'rotationY' in scene.environmentTexture) {
        (scene.environmentTexture as CubeTexture).rotationY = (settingsStore.envRotation * Math.PI) / 180;
    }

    // Apply image processing settings
    const config = scene.imageProcessingConfiguration;
    config.exposure = settingsStore.exposure;
    config.contrast = settingsStore.contrast;
    config.toneMappingEnabled = settingsStore.toneMappingEnabled;
    config.toneMappingType = settingsStore.toneMappingType;

    // Apply shadow ground
    viewer.groundShadow = settingsStore.groundShadow;

    // Apply camera settings
    const camera = scene.activeCamera as ArcRotateCamera | null;
    if (camera) {
        camera.beta = settingsStore.cameraBeta;

        // Editor-specific overrides: tight, immediate camera controls (no inertia/drift)
        camera.inertia = 0;
        camera.panningInertia = 0;
        camera.wheelDeltaPercentage = 0.025;
        camera.panningSensibility = 200;
        camera.angularSensibilityX = 200;
        camera.angularSensibilityY = 200;

        // Prevent FramingBehavior.zoomOnBoundingInfo from overwriting our
        // panningSensibility and wheelPrecision on every focus/reset call.
        const framingBehavior = camera.getBehaviorByName("Framing") as FramingBehavior | null;
        if (framingBehavior) {
            framingBehavior.autoCorrectCameraLimitsAndSensibility = false;
        }

        console.log('[Inspector Extension] Applied camera settings');
    }
};

/**
 * Trace Viewer Controls Component
 * React component that renders the trace-viewer property controls
 */
const TraceViewerControls: React.FC = () => {
    // Initialize from persistent store
    const [backgroundColor, setBackgroundColor] = useState(settingsStore.backgroundColor);
    const [cameraRadius, setCameraRadius] = useState(settingsStore.cameraRadius);
    const [cameraBeta, setCameraBeta] = useState(settingsStore.cameraBeta);
    const [alpha, setAlpha] = useState(settingsStore.alpha);
    const [prewarmParticles, setPrewarmParticles] = useState(settingsStore.prewarmParticles);
    const [environmentPreset, setEnvironmentPreset] = useState<EnvironmentPreset>(settingsStore.environmentPreset);
    const [customEnvUrl, setCustomEnvUrl] = useState(settingsStore.environmentUrl);
    const [iblIntensity, setIblIntensity] = useState(settingsStore.iblIntensity);
    const [envRotation, setEnvRotation] = useState(settingsStore.envRotation);
    const [exposure, setExposure] = useState(settingsStore.exposure);
    const [contrast, setContrast] = useState(settingsStore.contrast);
    const [toneMappingEnabled, setToneMappingEnabled] = useState(settingsStore.toneMappingEnabled);
    const [toneMappingType, setToneMappingType] = useState(settingsStore.toneMappingType);
    const [showSkybox, setShowSkybox] = useState(settingsStore.showSkybox);
    const [groundShadow, setGroundShadow] = useState(settingsStore.groundShadow);
    const [gridRatio, setGridRatio] = useState(settingsStore.gridRatio);
    const [gridMajorUnitFrequency, setGridMajorUnitFrequency] = useState(settingsStore.gridMajorUnitFrequency);
    const [gridMinorUnitVisibility, setGridMinorUnitVisibility] = useState(settingsStore.gridMinorUnitVisibility);
    const [gridOpacity, setGridOpacity] = useState(settingsStore.gridOpacity);
    const [debugSelectAnyNode, setDebugSelectAnyNode] = useState(settingsStore.debugSelectAnyNode);

    // Get reference to the trace-viewer element and its scene
    const getViewerElement = () => {
        return document.querySelector('trace-viewer');
    };

    // Apply changes directly to the scene
    const applyBackgroundColor = (color: string) => {
        settingsStore.backgroundColor = color; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            // Import Color3/Color4 from viewer's scene
            const scene = viewer.scene;

            // Parse hex color and apply to scene
            const r = parseInt(color.slice(1, 3), 16) / 255;
            const g = parseInt(color.slice(3, 5), 16) / 255;
            const b = parseInt(color.slice(5, 7), 16) / 255;

            // Preserve existing alpha or use current alpha state
            const currentAlpha = scene.clearColor?.a ?? alpha;

            scene.clearColor = new Color4(r, g, b, currentAlpha);

            // Also update the attribute for future reloads
            viewer.setAttribute('backgroundcolor', color);

            console.log('[Inspector Extension] Applied background color:', color, 'with alpha:', currentAlpha);
        }
    };

    const applyCameraRadius = (radius: number) => {
        settingsStore.cameraRadius = radius; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            const scene = viewer.scene;
            const camera = scene.activeCamera;

            if (camera instanceof ArcRotateCamera) {
                // Store the original radius if not already stored
                const cam = camera as ArcRotateCamera & { _originalRadius?: number };
                cam._originalRadius ??= camera.radius;

                // Apply radius as a multiplier of the original framed radius
                camera.radius = cam._originalRadius * radius;

                // Also update radius limits proportionally
                camera.lowerRadiusLimit = camera.radius * 0.05;
                camera.upperRadiusLimit = camera.radius * 5;

                console.log('[Inspector Extension] Applied camera radius scale:', radius, 'new radius:', camera.radius);
            }

            // Update attribute for future reloads
            viewer.setAttribute('cameraradius', radius.toString());
        }
    };

    const applyCameraBeta = (beta: number) => {
        settingsStore.cameraBeta = beta; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            const camera = viewer.scene.activeCamera;

            if (camera instanceof ArcRotateCamera) {
                camera.beta = beta;
                console.log('[Inspector Extension] Applied camera beta:', beta);
            }
        }
    };

    const applyAlpha = (alphaValue: number) => {
        settingsStore.alpha = alphaValue; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            const scene = viewer.scene;

            // Preserve existing RGB values
            const clearColor = scene.clearColor;
            if (clearColor) {
                scene.clearColor = new Color4(clearColor.r, clearColor.g, clearColor.b, alphaValue);
            }

            // Update attribute for future reloads
            viewer.setAttribute('alpha', alphaValue.toString());

            console.log('[Inspector Extension] Applied alpha:', alphaValue);
        }
    };

    const applyPrewarm = (prewarm: number) => {
        settingsStore.prewarmParticles = prewarm; // Persist to store

        const viewer = getViewerElement();
        if (viewer) {
            // Prewarm requires scene reload, just update the attribute
            viewer.setAttribute('prewarmParticles', prewarm.toString());
            console.log('[Inspector Extension] Set prewarm particles (requires reload):', prewarm);
        }
    };

    const applyEnvironment = (url: string) => {
        settingsStore.environmentUrl = url; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            const scene = viewer.scene;

            if (!url) {
                // Remove environment texture
                if (scene.environmentTexture) {
                    scene.environmentTexture.dispose();
                    scene.environmentTexture = null;
                }
                // Also remove skybox mesh since there's no environment to display
                // (but preserve showSkybox preference so it comes back when env is set)
                const existingSkybox = scene.getMeshByName('hdrSkyBox');
                if (existingSkybox) {
                    existingSkybox.dispose();
                }
                console.log('[Inspector Extension] Removed environment texture');
                return;
            }

            // Load HDR or ENV texture based on extension
            const isHdr = url.toLowerCase().endsWith('.hdr');

            // Dispose existing texture
            if (scene.environmentTexture) {
                scene.environmentTexture.dispose();
            }

            if (isHdr) {
                const envTexture = new HDRCubeTexture(url, scene, 512, false, true, true);
                scene.environmentTexture = envTexture;
                console.log('[Inspector Extension] Applied HDR environment:', url);
            } else {
                const envTexture = CubeTexture.CreateFromPrefilteredData(url, scene);
                scene.environmentTexture = envTexture;
                console.log('[Inspector Extension] Applied ENV environment:', url);
            }

            // Recreate skybox if it's currently shown
            if (settingsStore.showSkybox) {
                applySkybox(true);
            }

            // Update attribute for future reloads
            viewer.setAttribute('environment-image', url);
        }
    };

    const applyIblIntensity = (intensity: number) => {
        settingsStore.iblIntensity = intensity; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            viewer.scene.environmentIntensity = intensity;
            console.log('[Inspector Extension] Applied IBL intensity:', intensity);
        }
    };

    const applyEnvRotation = (rotation: number) => {
        settingsStore.envRotation = rotation; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene?.environmentTexture) {
            // Rotation in radians (0-360 degrees = 0-2π radians)
            const radians = (rotation * Math.PI) / 180;
            const envTex = viewer.scene.environmentTexture;
            if (envTex instanceof HDRCubeTexture || envTex instanceof CubeTexture) {
                envTex.rotationY = radians;
            }
            console.log('[Inspector Extension] Applied environment rotation:', rotation, 'degrees');
        }
    };

    const applyExposure = (value: number) => {
        settingsStore.exposure = value; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            viewer.scene.imageProcessingConfiguration.exposure = value;
            console.log('[Inspector Extension] Applied exposure:', value);
        }
    };

    const applyContrast = (value: number) => {
        settingsStore.contrast = value; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            viewer.scene.imageProcessingConfiguration.contrast = value;
            console.log('[Inspector Extension] Applied contrast:', value);
        }
    };

    const applyToneMapping = (enabled: boolean, type: number) => {
        settingsStore.toneMappingEnabled = enabled;
        settingsStore.toneMappingType = type;

        const viewer = getViewerElement();
        if (viewer?.scene) {
            const config = viewer.scene.imageProcessingConfiguration;
            config.toneMappingEnabled = enabled;
            config.toneMappingType = type;
            console.log('[Inspector Extension] Applied tone mapping:', enabled ? (type === 0 ? 'Standard' : 'ACES') : 'Disabled');
        }
    };

    const applySkybox = (show: boolean) => {
        settingsStore.showSkybox = show; // Persist to store

        const viewer = getViewerElement();
        if (viewer?.scene) {
            const scene = viewer.scene;

            // Find and dispose any existing skybox (Babylon names it 'hdrSkyBox')
            const existingSkybox = scene.getMeshByName('hdrSkyBox');
            if (existingSkybox) {
                existingSkybox.dispose();
            }

            if (show && scene.environmentTexture) {
                // createDefaultSkybox(environmentTexture, pbr, scale, blur)
                scene.createDefaultSkybox(scene.environmentTexture, true, 10000, 0.1);
                console.log('[Inspector Extension] Created skybox');
            } else {
                console.log('[Inspector Extension] Removed skybox');
            }
        }
    };

    const applyGroundShadow = (enabled: boolean) => {
        settingsStore.groundShadow = enabled;

        const viewer = getViewerElement();
        if (viewer) {
            viewer.groundShadow = enabled;
            console.log('[Inspector Extension] Shadow ground:', enabled);
        }
    };

    // Locate the live grid material on the utility layer scene.
    // The grid mesh is created in index.ts as a child of DefaultKeepDepthUtilityLayer.
    const getGridMaterial = (): GridMaterial | null => {
        const utilityScene = UtilityLayerRenderer.DefaultKeepDepthUtilityLayer.utilityLayerScene;
        const mat = utilityScene.getMeshByName('grid')?.material;
        return mat instanceof GridMaterial ? mat : null;
    };

    // The editor grid mesh is sized in world meters (scaling = 1), so gridRatio is directly
    // world meters per minor cell.
    const applyGridRatio = (value: number) => {
        settingsStore.gridRatio = value;
        const mat = getGridMaterial();
        if (mat) mat.gridRatio = value;
    };

    const applyGridMajorUnitFrequency = (value: number) => {
        settingsStore.gridMajorUnitFrequency = value;
        const mat = getGridMaterial();
        if (mat) mat.majorUnitFrequency = value;
    };

    const applyGridMinorUnitVisibility = (value: number) => {
        settingsStore.gridMinorUnitVisibility = value;
        const mat = getGridMaterial();
        if (mat) mat.minorUnitVisibility = value;
    };

    const applyGridOpacity = (value: number) => {
        settingsStore.gridOpacity = value;
        const mat = getGridMaterial();
        if (mat) mat.opacity = value;
    };


    return (
        <div style={{
            padding: '16px',
            color: '#ffffff',
            backgroundColor: '#252525',
            height: '100%',
            overflowY: 'auto',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <h2 style={{
                fontSize: '18px',
                marginBottom: '20px',
                borderBottom: '1px solid #444',
                paddingBottom: '8px'
            }}>
                Trace Viewer Settings
            </h2>

            {/* Background Color */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{
                    display: 'block',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: 500
                }}>
                    Background Color
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => {
                            const value = e.target.value;
                            setBackgroundColor(value);
                            applyBackgroundColor(value);
                        }}
                        style={{
                            width: '60px',
                            height: '32px',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    />
                    <input
                        type="text"
                        value={backgroundColor}
                        onChange={(e) => {
                            const value = e.target.value;
                            setBackgroundColor(value);
                            applyBackgroundColor(value);
                        }}
                        style={{
                            flex: 1,
                            padding: '6px 8px',
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            color: '#fff',
                            fontSize: '13px'
                        }}
                    />
                </div>
            </div>

            {/* Camera Radius */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{
                    display: 'block',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: 500
                }}>
                    Camera Radius: {cameraRadius.toFixed(2)}
                </label>
                <input
                    type="range"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={cameraRadius}
                    onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setCameraRadius(value);
                        applyCameraRadius(value);
                    }}
                    style={{
                        width: '100%'
                    }}
                />
            </div>

            {/* Camera Beta (Vertical Angle) */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{
                    display: 'block',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: 500
                }}>
                    Camera Angle: {cameraBeta.toFixed(2)}
                </label>
                <input
                    type="range"
                    min="0.1"
                    max="3.1"
                    step="0.05"
                    value={cameraBeta}
                    onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setCameraBeta(value);
                        applyCameraBeta(value);
                    }}
                    style={{
                        width: '100%'
                    }}
                />
                <div style={{
                    fontSize: '12px',
                    color: '#888',
                    marginTop: '4px'
                }}>
                    0 = top-down, π/2 = horizon, π = bottom-up
                </div>
            </div>

            {/* Alpha (Transparency) */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{
                    display: 'block',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: 500
                }}>
                    Background Alpha: {alpha.toFixed(2)}
                </label>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={alpha}
                    onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setAlpha(value);
                        applyAlpha(value);
                    }}
                    style={{
                        width: '100%'
                    }}
                />
            </div>

            {/* Prewarm Particles */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{
                    display: 'block',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: 500
                }}>
                    Prewarm Particles
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                        type="number"
                        min="0"
                        step="1"
                        value={prewarmParticles}
                        onChange={(e) => {
                            const value = parseInt(e.target.value, 10) || 0;
                            setPrewarmParticles(value);
                        }}
                        style={{
                            flex: 1,
                            padding: '6px 8px',
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            color: '#fff',
                            fontSize: '13px'
                        }}
                    />
                    <button
                        onClick={() => applyPrewarm(prewarmParticles)}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#0078d4',
                            border: 'none',
                            borderRadius: '4px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '13px'
                        }}
                    >
                        Apply
                    </button>
                </div>
                <div style={{
                    fontSize: '12px',
                    color: '#888',
                    marginTop: '4px'
                }}>
                    Note: Requires scene reload
                </div>
            </div>

            {/* Environment Map Section */}
            <div style={{
                marginTop: '32px',
                marginBottom: '24px',
                borderTop: '1px solid #444',
                paddingTop: '20px'
            }}>
                <h3 style={{
                    fontSize: '16px',
                    marginBottom: '16px',
                    fontWeight: 500
                }}>
                    Environment Map
                </h3>

                {/* Environment Preset */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Preset
                    </label>
                    <select
                        value={environmentPreset}
                        onChange={(e) => {
                            const preset = e.target.value as EnvironmentPreset;
                            setEnvironmentPreset(preset);
                            settingsStore.environmentPreset = preset;
                            const url = environmentPresets[preset];
                            setCustomEnvUrl(url);
                            applyEnvironment(url);
                        }}
                        style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            color: '#fff',
                            fontSize: '13px',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="none">None</option>
                        <option value="neutral">Neutral (Default)</option>
                        <option value="studio">Studio</option>
                        <option value="country">Country</option>
                        <option value="night">Night</option>
                        <option value="parking">Parking</option>
                        <option value="forest">Forest</option>
                    </select>
                </div>

                {/* Custom URL */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Custom URL
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                            type="text"
                            placeholder="https://example.com/environment.hdr"
                            value={customEnvUrl}
                            onChange={(e) => {
                                setCustomEnvUrl(e.target.value);
                                // Reset preset when entering custom URL
                                if (e.target.value && !(Object.values(environmentPresets) as string[]).includes(e.target.value)) {
                                    setEnvironmentPreset('none');
                                    settingsStore.environmentPreset = 'none';
                                }
                            }}
                            style={{
                                flex: 1,
                                padding: '6px 8px',
                                backgroundColor: '#2a2a2a',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                color: '#fff',
                                fontSize: '13px'
                            }}
                        />
                        <button
                            onClick={() => applyEnvironment(customEnvUrl)}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: '#0078d4',
                                border: 'none',
                                borderRadius: '4px',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Load
                        </button>
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: '#888',
                        marginTop: '4px'
                    }}>
                        Supports .hdr and .env files
                    </div>
                </div>

                {/* IBL Intensity */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        IBL Intensity: {iblIntensity.toFixed(2)}
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.05"
                        value={iblIntensity}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setIblIntensity(value);
                            applyIblIntensity(value);
                        }}
                        style={{
                            width: '100%'
                        }}
                    />
                </div>

                {/* Environment Rotation */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Env Rotation: {envRotation}°
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="360"
                        step="5"
                        value={envRotation}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setEnvRotation(value);
                            applyEnvRotation(value);
                        }}
                        style={{
                            width: '100%'
                        }}
                    />
                </div>

                {/* Show Skybox */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer'
                    }}>
                        <input
                            type="checkbox"
                            checked={showSkybox}
                            onChange={(e) => {
                                setShowSkybox(e.target.checked);
                                applySkybox(e.target.checked);
                            }}
                            style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                            }}
                        />
                        Show Skybox
                    </label>
                </div>

                {/* Ground Shadow */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer'
                    }}>
                        <input
                            type="checkbox"
                            checked={groundShadow}
                            onChange={(e) => {
                                setGroundShadow(e.target.checked);
                                applyGroundShadow(e.target.checked);
                            }}
                            style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                            }}
                        />
                        Ground Shadow
                    </label>
                </div>
            </div>

            {/* Grid Section */}
            <div style={{
                marginTop: '16px',
                marginBottom: '24px',
                borderTop: '1px solid #444',
                paddingTop: '20px'
            }}>
                <h3 style={{
                    fontSize: '16px',
                    marginBottom: '16px',
                    fontWeight: 500
                }}>
                    Grid
                </h3>

                <div style={{
                    fontSize: '12px',
                    color: '#888',
                    marginBottom: '16px',
                    lineHeight: '1.4'
                }}>
                    Minor lines are spaced in meters; every Nth line is bold.
                </div>

                {/* Grid Ratio */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Line Spacing: {gridRatio.toFixed(3)} m
                    </label>
                    <input
                        type="range"
                        min="0.01"
                        max="1"
                        step="0.01"
                        value={gridRatio}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setGridRatio(value);
                            applyGridRatio(value);
                        }}
                        style={{ width: '100%' }}
                    />
                </div>

                {/* Major Unit Frequency */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Major Line Every: {gridMajorUnitFrequency} lines ({(gridRatio * gridMajorUnitFrequency).toFixed(2)} m)
                    </label>
                    <input
                        type="range"
                        min="1"
                        max="20"
                        step="1"
                        value={gridMajorUnitFrequency}
                        onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            setGridMajorUnitFrequency(value);
                            applyGridMajorUnitFrequency(value);
                        }}
                        style={{ width: '100%' }}
                    />
                </div>

                {/* Minor Unit Visibility */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Minor Line Opacity: {gridMinorUnitVisibility.toFixed(2)}
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={gridMinorUnitVisibility}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setGridMinorUnitVisibility(value);
                            applyGridMinorUnitVisibility(value);
                        }}
                        style={{ width: '100%' }}
                    />
                </div>

                {/* Grid Opacity */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Overall Opacity: {gridOpacity.toFixed(2)}
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={gridOpacity}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setGridOpacity(value);
                            applyGridOpacity(value);
                        }}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>

            {/* Image Processing Section */}
            <div style={{
                marginTop: '16px',
                marginBottom: '24px',
                borderTop: '1px solid #444',
                paddingTop: '20px'
            }}>
                <h3 style={{
                    fontSize: '16px',
                    marginBottom: '16px',
                    fontWeight: 500
                }}>
                    Image Processing
                </h3>

                {/* Exposure */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Exposure: {exposure.toFixed(2)}
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="3"
                        step="0.05"
                        value={exposure}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setExposure(value);
                            applyExposure(value);
                        }}
                        style={{
                            width: '100%'
                        }}
                    />
                </div>

                {/* Contrast */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Contrast: {contrast.toFixed(2)}
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="3"
                        step="0.05"
                        value={contrast}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setContrast(value);
                            applyContrast(value);
                        }}
                        style={{
                            width: '100%'
                        }}
                    />
                </div>

                {/* Tone Mapping */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        fontWeight: 500
                    }}>
                        Tone Mapping
                    </label>
                    <select
                        value={toneMappingEnabled ? toneMappingType.toString() : 'disabled'}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === 'disabled') {
                                setToneMappingEnabled(false);
                                applyToneMapping(false, toneMappingType);
                            } else {
                                const type = parseInt(val, 10);
                                setToneMappingEnabled(true);
                                setToneMappingType(type);
                                applyToneMapping(true, type);
                            }
                        }}
                        style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            color: '#fff',
                            fontSize: '13px',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="disabled">Disabled</option>
                        <option value="0">Standard</option>
                        <option value="1">ACES</option>
                    </select>
                </div>
            </div>

            {/* Debug Section */}
            <div style={{
                marginTop: '16px',
                marginBottom: '24px',
                borderTop: '1px solid #444',
                paddingTop: '20px'
            }}>
                <h3 style={{
                    fontSize: '16px',
                    marginBottom: '16px',
                    fontWeight: 500
                }}>
                    Debug
                </h3>

                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer'
                    }}>
                        <input
                            type="checkbox"
                            checked={debugSelectAnyNode}
                            onChange={(e) => {
                                setDebugSelectAnyNode(e.target.checked);
                                settingsStore.debugSelectAnyNode = e.target.checked;
                            }}
                            style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                            }}
                        />
                        Select Any Node
                    </label>
                    <div style={{
                        fontSize: '12px',
                        color: '#888',
                        marginTop: '4px'
                    }}>
                        Allow selecting non-SceneObject nodes in the Inspector
                    </div>
                </div>
            </div>

            {/* Info Section */}
            <div style={{
                marginTop: '32px',
                padding: '12px',
                backgroundColor: '#1a1a1a',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#aaa',
                borderLeft: '3px solid #0078d4'
            }}>
                <strong style={{ color: '#fff' }}>Trace Viewer Controls</strong>
                <p style={{ margin: '8px 0 0 0', lineHeight: '1.4' }}>
                    These controls modify the trace-viewer component properties.
                    Some changes apply immediately, while others (like prewarm particles)
                    require reloading the scene.
                </p>
            </div>
        </div>
    );
};

// Simple icon component for the tab
const TraceViewerIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 1a2 2 0 0 1 2 2v2h1.5A1.5 1.5 0 0 1 13 6.5v7a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 13.5v-7A1.5 1.5 0 0 1 4.5 5H6V3a2 2 0 0 1 2-2zm1 2a1 1 0 0 0-2 0v2h2V3zM4.5 6a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5v-7a.5.5 0 0 0-.5-.5h-7z"/>
    </svg>
);

/**
 * Service definition for the Trace Viewer extension
 * This will be passed to ShowInspector via serviceDefinitions
 */
export const TraceViewerServiceDefinition: ServiceDefinition<[], [IShellService]> = {
    friendlyName: 'Trace Viewer Extension',

    // Consume the Shell service to add our side pane
    consumes: [ShellServiceIdentity],

    factory: (shellService: IShellService) => {
        console.log('[Inspector Extension] Initializing Trace Viewer extension...');
        shellServiceRef = shellService;

        // Add as a side pane on the right, top - same location as Properties/Debug/Stats
        const paneDefinition: SidePaneDefinition = {
            key: 'trace-viewer-settings',
            icon: TraceViewerIcon,
            content: TraceViewerControls,
            horizontalLocation: 'right',
            verticalLocation: 'top',
            order: -1, // Negative order to appear first
            title: 'Trace Viewer'
        };

        const disposable = shellService.addSidePane(paneDefinition);

        console.log('[Inspector Extension] Trace Viewer tab added to right pane');

        return {
            dispose: () => {
                console.log('[Inspector Extension] Disposing Trace Viewer extension');
                shellServiceRef = null;
                disposable.dispose();
            }
        };
    }
};
